import { FC, useContext } from "react";
import { Box, createStyles, makeStyles, Typography } from "@material-ui/core";
import { includes } from "lodash";
import moment from "moment";

import { EssentialImage } from "components";
import { useAssetDetailData } from "containers/AssetDetail/hooks";
import { AppContext } from "contexts";
import { NON_FAILED_DEAL_STATES } from "services/offers/constants";
import theme from "theme";

import IRRCalculator from "../IRRCalculator";
import ListingNote from "../ListingNote";
import OfferList from "../OfferList";
import ParagraphContent from "../ParagraphContent";
import AboutTable from "./AboutTable";
import OfferChart from "./OfferChart";
import OtherInfo from "./OtherInfo";
import RawDataDownload from "./RawDataDownload";
import ReturnsInfo from "./ReturnsInfo";
import SaleHistory from "./SaleHistory";

const useStyles = makeStyles(() =>
  createStyles({
    container: {
      width: "100%",
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "center",
    },
    section: {
      minWidth: "50%",
      maxWidth: "50%",
      [theme.breakpoints.down("sm")]: {
        minWidth: "100%",
      },
    },
    sectionTitle: {
      fontSize: "1.5rem",
      fontWeight: theme.typography.fontWeightBold,
      textTransform: "none",
    },
    aboutTableContainer: {
      maxWidth: "75%",
      [theme.breakpoints.down("md")]: {
        maxWidth: "85%",
      },
      [theme.breakpoints.down("sm")]: {
        maxWidth: "100%",
      },
    },
    listingNoteContainer: {
      maxWidth: "100%",
    },
    overviewContainer: {
      maxWidth: "85%",
      [theme.breakpoints.down("sm")]: {
        maxWidth: "100%",
      },
    },
  }),
);

interface AboutProps {
  isModal: boolean;
  listingId: number;
}

const About: FC<AboutProps> = ({ isModal, listingId }) => {
  const classes = useStyles();
  const { user } = useContext(AppContext);

  const {
    asset,
    listing,
    valuation,
    valuationDescription,
  } = useAssetDetailData(listingId);

  const { resale_info, sale_history } = asset || {};
  const {
    accepting_final_offers_start_at,
    buy_now_price,
    buy_now_price_history,
    earnings_file,
    deal,
    marketplace_median,
    offers,
    other_info,
    published_date,
    state,
  } = listing || {};
  const { regions, statistics_last_updated } = valuation;

  const isOffersSectionVisible = !!listing?.offers;

  const displayStatisticsLastUpdated = !!statistics_last_updated
    ? moment(statistics_last_updated).format("MM/DD/YYYY @ HH:mm")
    : "";

  const valuationDescriptionOverviewContent = valuationDescription?.contents.find(
    content => content.kind === "overview",
  );

  const filteredOffers = offers.filter(
    offer =>
      offer.kind !== "seller_counteroffer" ||
      (offer.kind === "seller_counteroffer" &&
        NON_FAILED_DEAL_STATES.includes(offer.state)) ||
      offer.id === listing.accepting_final_offers_initial_offer,
  );

  const hasBuyListingsPermission =
    user && includes(user.permissionList, "buy_listings");

  const shouldShowBuyerTools =
    !!hasBuyListingsPermission &&
    !(listing?.is_owner && listing?.kind === "direct_listing");

  return (
    <>
      {shouldShowBuyerTools && (
        <Box
          className={isOffersSectionVisible ? classes.listingNoteContainer : ""}
          textAlign={isOffersSectionVisible ? "left" : "center"}
        >
          <ListingNote
            listingId={listingId}
            isSecondaryMarketOwner={
              listing?.is_owner && listing?.kind === "secondary_listing"
            }
          />
        </Box>
      )}
      {shouldShowBuyerTools && <IRRCalculator listingId={listingId} />}
      <Box className={classes.container}>
        <Box className={isOffersSectionVisible ? classes.section : ""} mb={2}>
          <Box
            className={
              isOffersSectionVisible ? classes.aboutTableContainer : ""
            }
            textAlign={isOffersSectionVisible ? "left" : "center"}
          >
            <Typography
              variant="h3"
              className={classes.sectionTitle}
              gutterBottom
            >
              About This Listing
            </Typography>
            <Box mb={1}>
              <AboutTable
                asset={asset}
                listing={listing}
                valuation={valuation}
                isModal={isModal}
              />
            </Box>
            {displayStatisticsLastUpdated && (
              <Box mb={2} textAlign="center">
                <Typography color="textSecondary">
                  {deal
                    ? `The information on this listing was accurate as of the last earning statistics update on ${displayStatisticsLastUpdated}`
                    : `Earning statistics last updated ${displayStatisticsLastUpdated}`}
                </Typography>
              </Box>
            )}
            {!!earnings_file && (
              <Box mb={2} textAlign="center">
                <RawDataDownload earningsFile={earnings_file} />
              </Box>
            )}
            {!!resale_info && "last_update" in resale_info && (
              <Box mb={2} py={2} textAlign="center">
                <ReturnsInfo
                  purchasedCurrency={resale_info.currency}
                  resaleInfo={resale_info}
                />
              </Box>
            )}
            {!!sale_history.length && (
              <Box mb={2}>
                <SaleHistory saleHistory={sale_history} />
              </Box>
            )}
          </Box>
          <Box className={classes.overviewContainer}>
            {valuationDescriptionOverviewContent &&
              !!valuationDescriptionOverviewContent.body && (
                <>
                  <Typography
                    variant="h3"
                    className={classes.sectionTitle}
                    gutterBottom
                  >
                    Overview
                  </Typography>
                  <ParagraphContent>
                    <EssentialImage
                      htmlString={valuationDescriptionOverviewContent.body}
                    />
                  </ParagraphContent>
                </>
              )}
            {listing.performing_artist_analysis && (
              <>
                <Typography
                  variant="h3"
                  className={classes.sectionTitle}
                  gutterBottom
                >
                  Artist & Song Information
                </Typography>
                <ParagraphContent>
                  <EssentialImage
                    htmlString={listing.performing_artist_analysis}
                  />
                </ParagraphContent>
              </>
            )}
            <ParagraphContent>
              <OtherInfo otherInfo={other_info} regions={regions} />
            </ParagraphContent>
          </Box>
        </Box>
        {isOffersSectionVisible && (
          <Box className={classes.section} mb={2}>
            <>
              <Box mb={2}>
                <Typography
                  variant="h3"
                  className={classes.sectionTitle}
                  gutterBottom
                >
                  Price Tracker
                </Typography>
                <OfferChart
                  offers={filteredOffers}
                  marketplaceMedian={Number(marketplace_median) || 0}
                  buyNowPrice={buy_now_price}
                  buyNowPriceHistory={buy_now_price_history}
                  listingPublishedDate={published_date}
                  hasDeal={!!deal}
                  acceptingFinalOffersStartAt={accepting_final_offers_start_at}
                  listingState={state}
                />
              </Box>
              <OfferList
                displayProxyRow
                displayTitle
                listingId={listingId}
                maxOfferChainsToDisplay={!!valuationDescription ? 10 : 5}
              />
            </>
          </Box>
        )}
      </Box>
    </>
  );
};

export default About;
